import {
  Box,
  Flex,
  HStack,
  Icon,
  Image,
  Skeleton,
  Text,
  Tooltip,
} from '@chakra-ui/react'
import { t } from 'i18next'
import { useRef, useState } from 'react'
import { Link } from 'react-router'
import iconInboundArrow from '~/assets/svgs/icon-inbound-arrow.svg?react'
import iconOutboundArrow from '~/assets/svgs/icon-outbound-arrow.svg?react'
import ListItem from '~/components/ListItem'
import { HighlightText } from '~/components/shared/HighlightText'
import { light } from '~/constants/theme'
import {
  FaxListItemVariant,
  useFaxListItemStyle,
} from '~/pages/Fax/componentStyles/FaxListItem.style'
import { textStyleKeys } from '~/themes/textStyles'
import { formatRelativeDay, phoneFormatter } from '~/utils/formatter'

import { formatTimeString, toNounCasing } from '@mangovoice/core'
import { FaxDirection, FaxInstance, FaxStatus } from '@mangovoice/types'

const IMAGE_BOX_SIZE_REMS = 5.5625
const IMAGE_BOX_SIZE_PX = IMAGE_BOX_SIZE_REMS * 16

const getStatusVariant = (status: FaxStatus) => {
  switch (status) {
    case FaxStatus.Failed:
      return FaxListItemVariant.Failed
    case FaxStatus.Pending:
      return FaxListItemVariant.Queued
    default:
      return FaxListItemVariant.Normal
  }
}

const getFaxItemStatus = (faxStatus: FaxStatus, faxDirection: FaxDirection) => {
  if (faxStatus === FaxStatus.Failed) {
    return t('faxViewList.status.failed')
  }

  if (faxDirection === FaxDirection.Outbound) {
    if (faxStatus === FaxStatus.Pending) {
      return t('faxViewList.status.pending')
    }
    return t('faxViewList.status.sent')
  }

  if (faxDirection === FaxDirection.Inbound) {
    return t('faxViewList.status.received')
  }

  return t('faxViewList.status.unknown')
}

interface FaxListViewItemProps {
  fax: FaxInstance
  isSelected: boolean
  searchTerm?: string
}

export const FaxListItem = ({
  fax,
  isSelected,
  searchTerm,
}: FaxListViewItemProps) => {
  const styles = useFaxListItemStyle({
    variant: getStatusVariant(fax.status),
  })

  const imageRef = useRef<HTMLImageElement>()
  const [scaledImageDimensions, setScaledImageDimensions] = useState<{
    height: number
    width: number
  }>()
  const isOutbound = fax.direction === FaxDirection.Outbound

  const showNumber = isOutbound
    ? fax.to_phone_number.phone_number
    : fax.from_phone_number.phone_number

  const icon = isOutbound ? iconOutboundArrow : iconInboundArrow

  const faxNumber: string = phoneFormatter(showNumber)

  const statusString = getFaxItemStatus(fax.status, fax.direction)
  const isPending = fax.status === FaxStatus.Pending

  return (
    <Link to={`/faxes/${fax.id}`}>
      <ListItem
        activated={isSelected}
        aria-selected={isSelected}
        date={
          <Text sx={{ textStyle: textStyleKeys.Body3 }}>
            {formatRelativeDay(fax.created_at)}
          </Text>
        }
        height={`${IMAGE_BOX_SIZE_REMS}rem`}
        styleType="fax"
        Thumbnail={
          <Flex
            width={`${IMAGE_BOX_SIZE_REMS}rem`}
            sx={styles.thumbnailContainer}
          >
            <Image
              src={fax.thumbnail_url}
              boxSize={`${IMAGE_BOX_SIZE_REMS}rem`}
              loading="lazy"
              sx={styles.thumbnailImage}
              onLoad={(e) => {
                imageRef.current = e.target as HTMLImageElement
                const scaleRatios = {
                  width: IMAGE_BOX_SIZE_PX / imageRef.current.naturalWidth,
                  height: IMAGE_BOX_SIZE_PX / imageRef.current.naturalHeight,
                }

                //which is smaller, the width or the height?
                const matchScale =
                  scaleRatios.width > scaleRatios.height
                    ? scaleRatios.height
                    : scaleRatios.width

                const scaled = {
                  height: imageRef.current.naturalHeight * matchScale,
                  width: imageRef.current.naturalWidth * matchScale,
                }

                setScaledImageDimensions(scaled)
              }}
            />
            {!scaledImageDimensions && (
              <Box sx={styles.skeletonContainer}>
                <Skeleton sx={styles.skeleton} />
              </Box>
            )}
            {scaledImageDimensions && (
              <Box
                height={scaledImageDimensions.height}
                width={scaledImageDimensions.width}
                sx={styles.thumbnailBox}
              />
            )}
          </Flex>
        }
        shouldShowUnreadDot={null}
        subTitle={
          <HStack spacing="0.5rem">
            <Icon as={icon} sx={styles.directionIcon} />
            <Tooltip
              hasArrow
              label={isPending ? t('fax.tooltip.pending') : undefined}
            >
              <Text as="span" sx={styles.statusText}>
                {toNounCasing(statusString)}
              </Text>
            </Tooltip>
          </HStack>
        }
        time={
          <Text sx={{ textStyle: textStyleKeys.Body3 }}>
            {formatTimeString(fax.created_at)}
          </Text>
        }
        title={
          <HighlightText
            sx={styles.numberText}
            permutationFilters={['-', ' ', '(', ')']}
            query={searchTerm}
            color={light.dark900}
            fontWeight={fax.read_at ? 'normal' : 'bold'}
          >
            {faxNumber}
          </HighlightText>
        }
      />
    </Link>
  )
}
